import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import io from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';

import ringback from './../ringback';
import unlockDevice from './../sclakWEB';
import CallScreen from './CallScreen';

// TODO: enable/disable audio
// TODO: enable/disable video

const Call = ({ baseURL, buildingId, residentId, residentName, history }) => {
  // const peerUser = { id: residentId, name: residentName };
  const currentUser = { id: 'DoorbellWeb', name: 'DoorbellWeb' };

  const socket = useMemo(() => io(baseURL), [baseURL]);

  const localStreamRef = useRef({ toURL: () => null });
  const localTrackVideoDimensionsRef = useRef(null);
  const [isReadyToStreamOUT, readyToStreamOUT] = useState(false);

  const remoteStreamRef = useRef({ toURL: () => null });
  const remoteTrackVideoDimensionsRef = useRef(null);
  const [isReadyToStreamIN, readyToStreamIN] = useState(false);

  const [isRinging, setIsRinging] = useState(false);
  const [doorToUnlock, setDoorToUnlock] = useState(null);

  const rtcPeerConnection = useMemo(
    () =>
      new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' },
          { urls: 'stun:stun.ekiga.net' },
          { urls: 'stun:stun.ideasip.com' },
          { urls: 'stun:stun.rixtelecom.se' },
          { urls: 'stun:stun.schlund.de' },
          { urls: 'stun:stun.voiparound.com' },
          { urls: 'stun:stun.voipbuster.com' },
          { urls: 'stun:stun.voipstunt.com' },
          { urls: 'stun:stun.voxgratia.org' },
          { urls: 'stun:doorkeeper.phoney.io:5349' },
          {
            urls: 'turn:doorkeeper.phoney.io:5349',
            username: 'doorbell',
            credential: 'doorbell',
          },
        ],
      }),
    [],
  );

  const streamConstraints = {
    audio: true,
    video: true,
  };

  const goBack = useCallback(() => {
    ringback
      .then((tune) => {
        tune.stop();
        history.goBack();
      })
      .catch((e) => {
        history.goBack();
      });
  }, [history]);

  const hangUp = useCallback(() => {
    if (socket) {
      socket.emit('hang-up', residentId);
      goBack();
    }
  }, [socket, residentId, goBack]);

  const unlock = useCallback(async () => {
    if (socket) {
      await unlockDevice(doorToUnlock.id, doorToUnlock.secret);
      socket.emit('door-opened', residentId);
    }
  }, [socket, doorToUnlock, residentId]);

  const detectLocalStream = useCallback(() => {
    // TODO: handle when navigator.mediaDevices is null
    return navigator.mediaDevices
      .getUserMedia(streamConstraints)
      .then((stream) => {
        stream.getVideoTracks().forEach((videoTrack) => {
          console.log(`Video device: ${videoTrack.label}`, videoTrack.getSettings());
        });
        stream.getAudioTracks().forEach((audioTrack) => {
          console.log(`Audio device: ${audioTrack.label}`, audioTrack.getSettings());
        });
        return stream;
      })
      .catch((err) => {
        console.error(err);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    socket.on('connect', () => {
      console.log(`Connected to the signaling server`);

      const isCaller = true;
      socket.emit('join', residentId, buildingId, isCaller);
    });

    socket.on('created', () => {
      console.log('Caller received the `created` event');

      detectLocalStream().then((stream) => {
        console.log('Caller is ready to stream and will ring the called');
        socket.emit('ring', residentId, buildingId, uuidv4(), currentUser);
        localStreamRef.current = stream;
        stream
          .getVideoTracks()
          .map((track) => {
            const { width, height } = track.getSettings();
            return { width, height };
          })
          .forEach((videoDimensions) => {
            localTrackVideoDimensionsRef.current = videoDimensions;
            socket.emit('advertise-video-dimensions', residentId, videoDimensions);
          });
        readyToStreamOUT(true);
      });
    });

    socket.on('joined', () => {
      console.error('Caller is not supposed to receive `joined` event');
    });

    socket.on('missing', () => {
      // TODO: better communication with the user
      console.error('Resident called is missing in the building!');
      goBack();
    });

    socket.on('full', () => {
      // TODO: better communication with the user
      console.log('Resident called is busy');
      goBack();
    });

    socket.on('rejected', () => {
      // TODO: better communication with the user
      console.log('Resident called refused the call');
      goBack();
    });

    socket.on('hang-up', () => {
      // TODO: better communication with the user
      console.log('Peer hung-up the call');
      goBack();
    });

    socket.on('open-door', async (door) => {
      console.log('Caller was granted the right to open the door');
      console.assert(door.id, 'Missing door.id in message open-door');
      console.assert(door.secret, 'Missing door.secret in message open-door');
      setDoorToUnlock(door);
    });

    socket.on('advertise-video-dimensions', (remoteTrackVideoDimensions) => {
      console.log('Peer advertised video dimensions', remoteTrackVideoDimensions);
      remoteTrackVideoDimensionsRef.current = remoteTrackVideoDimensions;
    });

    socket.on('ready', () => {
      setIsRinging(false);
      rtcPeerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          console.log('Caller received ICE candidate');
          socket.emit('candidate', event.candidate, residentId);
        }
      };
      // NOTE: needed on iOS devices, onaddstream is never called
      rtcPeerConnection.ontrack = (event) => {
        if (event.track) {
          console.log('Caller received remote', event.track);
          if (remoteStreamRef.current instanceof MediaStream) {
            remoteStreamRef.current.addTrack(event.track);
          } else {
            remoteStreamRef.current = new MediaStream([event.track]);
          }
          readyToStreamIN(true);
        }
      };
      rtcPeerConnection.onaddstream = (event) => {
        if (event.stream) {
          console.log('Caller received remote', event.stream);
          remoteStreamRef.current = event.stream;
          readyToStreamIN(true);
        }
      };
      // TODO: getTracks is marked as experimental on MDN, replace
      // with getAudiotracks && getVideotracks, see
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaStream/getTracks
      localStreamRef.current.getTracks().forEach((track) => rtcPeerConnection.addTrack(track, localStreamRef.current));
      rtcPeerConnection
        .createOffer()
        .then((offer) => {
          rtcPeerConnection
            .setLocalDescription(offer)
            .then(() => {
              console.log('Caller has an offer to send');
              socket.emit('offer', offer, residentId);
            })
            .catch((e) => console.error(e));
        })
        .catch((e) => console.error(e));
    });

    socket.on('offer', (offer) => {
      console.error('Caller is not supposed to receive `offer` event');
    });

    socket.on('answer', (answer) => {
      console.log('Caller received the `answer` event');
      ringback.then((tune) => tune.stop());
      rtcPeerConnection.setRemoteDescription(answer).catch((e) => console.error(e));
    });

    socket.on('candidate', (candidate) => {
      console.log('Received ICE candidate');
      rtcPeerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    });

    socket.on('disconnect', () => {
      console.log('Caller disconnected from the signaling server');
    });

    socket.on('connect_error', (e) => {
      console.log('Error from signaling server', e);
    });

    return () => {
      // TODO: extract freeResources function
      if (socket) {
        socket.disconnect();
      }
      if (rtcPeerConnection) {
        rtcPeerConnection.close();
      }
      if (localStreamRef.current instanceof MediaStream) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = { toURL: () => null };
        readyToStreamOUT(false);
      }
      if (remoteStreamRef.current instanceof MediaStream) {
        remoteStreamRef.current.getTracks().forEach((track) => track.stop());
        remoteStreamRef.current = { toURL: () => null };
        readyToStreamIN(false);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CallScreen
      residentName={residentName}
      localStream={localStreamRef.current}
      remoteStream={remoteStreamRef.current}
      localTrackVideoDimensions={localTrackVideoDimensionsRef.current}
      remoteTrackVideoDimensions={remoteTrackVideoDimensionsRef.current}
      isReadyToStreamIN={isReadyToStreamIN}
      isReadyToStreamOUT={isReadyToStreamOUT}
      isRinging={isRinging}
      canUnlock={!!doorToUnlock}
      audioEnabled={true}
      videoEnabled={true}
      onUnlock={unlock}
      onHangUp={hangUp}
      onToggleAudio={() => console.log('toggleAudio')}
      onToggleVideo={() => console.log('toggleVideo')}
    />
  );
};

export default Call;
